/**
 * The client-side book of what is still lying on the ground.
 *
 * WHY this exists: `Drops.sync(list)` is a full reconcile — it DESTROYS every
 * entity whose id is missing from the list it is handed. farm.ts used to hand it
 * only the newest batch, merged with a `knownDrops` array that was declared and
 * never assigned (so permanently `[]`). The result destroyed the player's work:
 * harvest a plot, then click the next one, and the first harvest's drops were
 * wiped off the ground before he could walk over them. They must sit there for
 * their whole TTL.
 *
 * So every batch is ACCUMULATED here, and the full live set is what goes to
 * sync(). Two things must then also be true, or accumulation reintroduces a worse
 * bug — resurrection:
 *   - a claimed drop is `forget`-ten, or the next batch's sync respawns it and the
 *     player picks up loot the server already banked (and then gets 'Already
 *     picked up' forever);
 *   - an expired drop is pruned, using the deadline derived from the SERVER's
 *     `expiresInMs`, not a second hardcoded TTL on the client.
 *
 * Pure bookkeeping on purpose: jsdom cannot drive Pixi, so this is the part that
 * can actually be pinned by tests.
 */
import type { GroundDropData } from '../../map/drops';

export interface DropRegistry {
  /**
   * Fold a freshly-reported batch in and return the complete live set to hand to
   * `Drops.sync`. `now` is a `performance.now()`-style clock, passed in so tests
   * are deterministic.
   */
  add(list: GroundDropData[], now: number): GroundDropData[];
  /** Claimed, or gone for any other reason. Stops sync() from resurrecting it. */
  forget(id: string): void;
  /** The set still alive at `now`, expiries pruned. */
  live(now: number): GroundDropData[];
}

export function createDropRegistry(): DropRegistry {
  /** id → drop + its absolute deadline on the same clock `add`/`live` are given. */
  const known = new Map<string, { data: GroundDropData; expiresAt: number }>();

  const prune = (now: number) => {
    for (const [id, e] of known) if (e.expiresAt <= now) known.delete(id);
  };

  const snapshot = (now: number): GroundDropData[] =>
    [...known.values()].map((e) => ({ ...e.data, expiresInMs: e.expiresAt - now }));

  return {
    add(list, now) {
      prune(now);
      for (const d of list) {
        // Re-reporting a drop we already track (a poll overlapping a producer's
        // response) must NOT extend its life: keep the first deadline we computed.
        if (known.has(d.id)) continue;
        known.set(d.id, { data: d, expiresAt: now + d.expiresInMs });
      }
      return snapshot(now);
    },
    forget(id) { known.delete(id); },
    live(now) { prune(now); return snapshot(now); },
  };
}
